import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { useMemo } from 'react'
import { CurrencyCode, CurrencySymbol } from 'Utils/types/currencies'
import { isNonZeroAmount, parseAmount } from 'Utils/helpers/currency'
import { EmployerAdminInvoice } from 'Utils/types/invoice'
import endOfDay from 'date-fns/endOfDay'
import { firstOfFollowingMonth, firstOfMonth, formatDateForServer } from 'Utils/helpers/dateUtils'
import { findLatestInvoiceDate } from 'Utils/helpers/invoice'
import { toast } from 'react-toastify'
import { useAddUserAllowanceMutation } from 'Hooks/api/JOONAdmin/useAddUserAllowanceMutation'
import { User } from 'Utils/types/user'
import { BenefitProgram } from 'Utils/types/benefitProgram'

export type AllowanceFormValues = {
  amount: string
  start: Date
  end?: Date
  reason: string
}

type UseAllowanceFormArgs = {
  currencyCode: CurrencyCode
  invoices: EmployerAdminInvoice[]
  userId: User['id']
  benefitProgramId: BenefitProgram['id']
  membershipEndDate?: Date
}

export const useAllowanceForm = ({
  currencyCode,
  invoices,
  userId,
  benefitProgramId,
  membershipEndDate
}: UseAllowanceFormArgs) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<AllowanceFormValues>()

  const { isLoading, mutateAsync } = useAddUserAllowanceMutation()

  const earliestAllowanceStartDate = useMemo(() => {
    const latestInvoiceDate = findLatestInvoiceDate(invoices)

    return latestInvoiceDate ? firstOfFollowingMonth(latestInvoiceDate) : firstOfMonth(new Date())
  }, [invoices])

  const latestAllowanceEndDate = useMemo(
    () => (membershipEndDate ? endOfDay(membershipEndDate) : undefined),
    [membershipEndDate]
  )

  const currencySymbol = useMemo(() => CurrencySymbol[currencyCode], [currencyCode])

  const registerOptions = {
    start: { required: 'A start date for the allowance is required.' },
    reason: { required: 'A reason for creating this allowance is required.' },
    amount: {
      validate: (value: unknown) =>
        isNonZeroAmount(value) || 'A valid, non-zero amount is required.'
    }
  }

  const handleSubmission: SubmitHandler<AllowanceFormValues> = async ({
    amount,
    start,
    end,
    reason
  }) => {
    const response = await mutateAsync({
      userId,
      benefitProgramId,
      amount: `${parseAmount(amount)}`,
      start: formatDateForServer(start),
      end: end ? formatDateForServer(end) : null,
      reason
    })

    // Reset form upon successful allowance modification
    if (response.success) {
      reset()
    }
  }

  const handleError: SubmitErrorHandler<AllowanceFormValues> = (errors) => {
    if (errors.amount?.message) toast.error(errors.amount.message)
    if (errors.start?.message) toast.error(errors.start.message)
    if (errors.reason?.message) toast.error(errors.reason.message)
  }

  return {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    errors,
    earliestAllowanceStartDate,
    latestAllowanceEndDate,
    registerOptions,
    currencySymbol,
    isLoading,
    handleError,
    handleSubmission
  }
}
